import { Component, OnInit, inject, DestroyRef, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { CardCampanaPorcentajeComponent } from '@/app/features/campaña/components/card-campana-porcentaje-component/card-campana-porcentaje-component';
import { CardCampanaComponent } from '@/app/features/campaña/components/card-campana-component/card-campana-component';
import { CampanaSelectComponent, Option } from '@/app/features/campaña/components/campana-select/campana-select-component';
import { CampaignStoreService } from '@/app/features/campaña/services/campaign-store.service';
import { CampaignClient } from '@/app/features/campaña/models/campaign-client.interface';
import { formatearFecha } from '@/app/utils/string-utils';
import {UserRole, UserRoleStore} from '@/app/core/states/user-role-stare/client-role-state.store';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { ThemeService } from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-campana-component',
  standalone: true,
  imports: [CardCampanaComponent, CardCampanaPorcentajeComponent, CampanaSelectComponent, EmptyStateComponent, NgClass, AsyncPipe],
  templateUrl: './campana-component.html',
  styleUrls: ['./campana-component.scss'],
})
export class CampanaComponent implements OnInit {
  private router:Router = inject(Router);
  private store:CampaignStoreService = inject(CampaignStoreService);
  readonly destroyRef:DestroyRef = inject(DestroyRef);
  private userState = inject(UserRoleStore);
  private themeService = inject(ThemeService);

  protected themeContent = this.themeService.themeContent;
  protected campanaContent = computed(() => this.themeContent()?.clientCampaignsComponent);


  messageCampana = '';

  campaigns: CampaignClient[] = [];
  campaignOptions: Option[] = [];
  selectedCampaignOption?: Option;
  selectedCampaign: CampaignClient | null = null;

  protected tabla: any = {};
  cardData$ = this.store.summaryCards$;
  percentageCard$ = this.store.percentageCard$;

  ngOnInit(): void {

    const user: UserRole | null= this.userState.user();
    if(user === null){
      return;
    }

    // Usar el publicId del cliente desde los atributos del usuario
    const clientPublicId = user.attributes?.['publicId'] || user.id;

    // Inicializar tabla con responsable vacío
    this.tabla = {
      responsable: 'Cargando...'
    };

    this.store.loadCampaigns(clientPublicId ?? "");

    // Se limpia cuando recarga la pagina
    this.store.setSelectedCampaign(null);

    // Lista raw (para armar UI)
    this.store.campaignsRaw$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(list => {
        this.campaigns = list;
        if (this.campaigns.length > 0) {
          this.messageCampana = 'Seleccione una campaña';
        } else {
          this.messageCampana = 'No tiene campañas asignadas';
        }
      });

    // Opciones del dropdown
    this.store.campaignsOptions$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(opts => this.campaignOptions = opts);

    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(opt => {
        this.selectedCampaignOption = opt ?? undefined;
        const camp = opt ? this.store.getCampaignByPublicId(opt.value) : null;
        this.selectedCampaign = camp;

        if (camp) {
          this.tabla = {
            fechaInicio: formatearFecha(camp.startDate),
            fechaFin: formatearFecha(camp.endDate),
            duracionTotal: this.getDuracionTotal(camp.startDate, camp.endDate),
            diasRestantes: this.getDiasRestantes(camp.endDate),
            responsable: this.tabla.responsable, // Mantener el responsable ya cargado
          };
        }
      });

    // Obtener información completa del cliente para obtener el contactName (responsable) - cacheado en store
    this.store.client$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(client => {
        if (client) {
          this.updateResponsable(client.contactName || '—');
        } else {
          this.updateResponsable('—');
        }
      });
  }

  onCampaignSelect(opt: Option) {
    this.store.setSelectedCampaign(opt);
  }

  getDiasRestantes(fechaFin: string): number {
    const fin = new Date(fechaFin);
    const hoy = new Date();
    const diff = fin.getTime() - hoy.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  protected goToResultados(): void {
    if (!this.selectedCampaign) return;

    this.router.navigate(
      ['/resultados'],
      {
        state: {selectedCampaign: this.selectedCampaign}
      }
    );
  }

  private getDuracionTotal(fechaInicio: string, fechaFin: string): number {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    const diff = fin.getTime() - inicio.getTime();
    return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  private updateResponsable(responsable: string): void {
    // Asegurar que tabla existe y tiene la propiedad responsable
    if (!this.tabla) {
      this.tabla = {};
    }

    // Actualizar el responsable
    this.tabla.responsable = responsable;
  }

}
